import { FamilyAccount, Patient, PatientStatusEnum } from "@mgdx/shared/src/models/Patient";
import * as Sentry from "@sentry/react";
import { useStore } from "zustand";
import { createStore } from "zustand/vanilla";

import { persistStore } from "./persistStore";
import { talkStore } from "./sharedStores";

export type CurrentUserState = {
  userId: number;
  userFirstName: string;
  userLastName: string;
  userInfo?: Patient;
  isEmailVerified: boolean;
  isLoggedIn: boolean;
  isLoggedInAndValidAccessToken: boolean;
  isRegistered: boolean;
  inProcessOfSignup: boolean;
  hasMedicineNotebookAccount: boolean;
  isLiffLogined: boolean;
  isInLineApp: boolean;
  isLoginChecked: boolean;
  hasInterviewSheetSetting: boolean;
  favoritePharmacyIds: number[];
  favoriteClinicIds: number[];
  needsShowMigrateMedicineNotebook: boolean;
  isInMedicineNotebookCreateFlow: boolean;
};

type CurrentUserActions = {
  setCurrentUser: (patient: Patient) => void;
  setLiffLogined: (isLiffLogined: boolean) => void;
  setInLineApp: (isInLineApp: boolean) => void;
  setInProcessOfSignup: (inProcessOfSignup: boolean) => void;
  clearCurrentUser: () => void;
  clearPatientInfo: () => void;
  setFavoritePharmacyIds: (favoritePharmacyIds: number[]) => void;
  setFavoriteClinicIds: (favoriteClinicIds: number[]) => void;
  optimisticallyAddFamilyAccount: (familyAccount: FamilyAccount) => void;
  setNeedsShowMigrateMedicineNotebook: (needsShowMigrateMedicineNotebook: boolean) => void;
  setIsInMedicineNotebookCreateFlow: (isInMedicineNotebookCreateFlow: boolean) => void;
};

export const initialCurrentUserState: CurrentUserState = {
  userId: 0,
  userFirstName: "",
  userLastName: "",
  userInfo: undefined,
  isEmailVerified: false,
  isLiffLogined: false,
  isLoggedIn: false,
  isLoggedInAndValidAccessToken: false,
  isRegistered: false,
  inProcessOfSignup: false,
  hasMedicineNotebookAccount: false,
  isLoginChecked: true,
  isInLineApp: false,
  hasInterviewSheetSetting: false,
  favoritePharmacyIds: [],
  favoriteClinicIds: [],
  needsShowMigrateMedicineNotebook: false,
  isInMedicineNotebookCreateFlow: false,
};

// 保存データがない起動直後は、ログインの確認が終わるまで未確認として扱う
const startupCurrentUserState: CurrentUserState = { ...initialCurrentUserState, isLoginChecked: false };

export const currentUserStore = createStore<CurrentUserState & { actions: CurrentUserActions }>()((set) => ({
  ...startupCurrentUserState,
  actions: {
    setCurrentUser: (patient) =>
      set((state) => ({
        userId: patient.id,
        userFirstName: patient.firstName ?? "",
        userLastName: patient.lastName ?? "",
        userInfo: { ...state.userInfo, ...patient },
        isLoggedIn: true,
        isLoggedInAndValidAccessToken: true,
        isRegistered: patient.status === PatientStatusEnum.Registered || patient.status === PatientStatusEnum.Verified,
        hasMedicineNotebookAccount: (patient.medicineNotebooks || []).length > 0, // FIXME= 同一アカウント横断でのOEMアプリの利用の際は修正が必要
        isLoginChecked: true,
        hasInterviewSheetSetting: Boolean(patient.wantsGeneric || patient.hasMedicineNotebook),
        isEmailVerified: patient.status === PatientStatusEnum.Verified,
        favoritePharmacyIds: patient.favoritePharmacies || [],
        favoriteClinicIds: patient.favoriteClinics || [],
      })),
    setLiffLogined: (isLiffLogined) => set({ isLiffLogined }),
    setInLineApp: (isInLineApp) => set({ isInLineApp }),
    setInProcessOfSignup: (inProcessOfSignup) => set({ inProcessOfSignup }),
    clearCurrentUser: () => set(initialCurrentUserState),
    // 別の患者への切り替え中に前の患者の個人情報を見せない。ログイン状態のフラグは画面の遷移やリダイレクトに使うため残す
    clearPatientInfo: () =>
      set({
        userId: initialCurrentUserState.userId,
        userFirstName: initialCurrentUserState.userFirstName,
        userLastName: initialCurrentUserState.userLastName,
        userInfo: initialCurrentUserState.userInfo,
        favoritePharmacyIds: initialCurrentUserState.favoritePharmacyIds,
        favoriteClinicIds: initialCurrentUserState.favoriteClinicIds,
        // 新しい患者の情報が入るまで、患者 ID を使う取得を止める
        isLoggedInAndValidAccessToken: false,
      }),
    setFavoritePharmacyIds: (favoritePharmacyIds) => set({ favoritePharmacyIds }),
    setFavoriteClinicIds: (favoriteClinicIds) => set({ favoriteClinicIds }),
    optimisticallyAddFamilyAccount: (familyAccount) =>
      set(({ userInfo }) => ({
        userInfo: { ...(userInfo as Patient), familyAccount: [...(userInfo?.familyAccount ?? []), familyAccount] },
      })),
    setNeedsShowMigrateMedicineNotebook: (needsShowMigrateMedicineNotebook) =>
      set({ needsShowMigrateMedicineNotebook }),
    setIsInMedicineNotebookCreateFlow: (isInMedicineNotebookCreateFlow) => set({ isInMedicineNotebookCreateFlow }),
  },
}));

persistStore(
  currentUserStore,
  {
    key: "persist:currentUser",
    // 氏名・患者情報とトークンの有効性、フローの途中だけ使うフラグは保存しない。氏名と患者情報は起動後に取り直す
    fields: [
      "userId",
      "isEmailVerified",
      "isLoggedIn",
      "isRegistered",
      "hasMedicineNotebookAccount",
      "isLiffLogined",
      "isInLineApp",
      "isLoginChecked",
      "hasInterviewSheetSetting",
      "favoritePharmacyIds",
      "favoriteClinicIds",
    ],
  },
  startupCurrentUserState
);

/**
 * 患者情報を currentUser に反映し、Sentry のユーザーの設定とトークの有効化も行う。React の外からも呼べる。
 */
export const setCurrentUser = (patient: Patient) => {
  Sentry.getCurrentScope().setUser({ id: patient.id.toString() });
  currentUserStore.getState().actions.setCurrentUser(patient);
  talkStore.getState().actions.enableTalk();
};

export const useCurrentUserActions = () => useStore(currentUserStore, (state) => state.actions);

// action は store の生成時に一度だけ作られるため、React の外からも直接呼べるよう個別に export する
export const {
  setLiffLogined,
  setInLineApp,
  setInProcessOfSignup,
  clearCurrentUser,
  setFavoritePharmacyIds,
  setFavoriteClinicIds,
  optimisticallyAddFamilyAccount,
  setNeedsShowMigrateMedicineNotebook,
  setIsInMedicineNotebookCreateFlow,
} = currentUserStore.getState().actions;
